/**
 * A Team Assist V2 answer as the panel renders it, built from glados's turn
 * stream (`reduceTeamAssistV2Event`) or from a stored answer
 * (`answerFromStoredMessage`). Plain objects, never mutated.
 *
 * `blocks` are in the order the model wrote them, each carrying the `index`
 * glados gave it: `text` and `suggested_reply` hold `text`, and `tool_use`
 * holds the call (`toolUseId`, `name`, `input`) and, when its result surfaced
 * as a card, `execution` (`result`, `resultType`, `isError`). A call the turn
 * ended on for the agent to run or reject also holds `proposal`
 * (`displayName`, `summary`, `inputSchema`, the agent's `decision`, the
 * `approvedInput` it ran with, and, when glados last refused to run it, the
 * `refusal` code and any `issues` it found in the input).
 * `rating` is the agent's thumbs on a finished answer, in V1's shape
 * (`{ value, reason?, comment? }`, see `TeamAssistRating`), or null.
 */

export const TeamAssistV2AnswerStatus = {
  streaming: 'streaming',
  done: 'done',
  error: 'error',
};

// Why an answer failed. Only `server` carries a message, glados's own.
export const TeamAssistV2ErrorCode = {
  server: 'server',
  requestFailed: 'request_failed',
  network: 'network',
  interrupted: 'interrupted',
};

export const TeamAssistV2ProposalStatus = {
  pending: 'pending',
  running: 'running',
  // Approved with no outcome, and no run streaming: the run's request died
  // mid-call, or another tab holds it. glados accepts neither Run nor Reject.
  unconfirmed: 'unconfirmed',
  succeeded: 'succeeded',
  failed: 'failed',
  rejected: 'rejected',
};

// What the agent decided on a proposal; null until they do.
export const TeamAssistV2ProposalDecision = {
  approved: 'approved',
  rejected: 'rejected',
};

export function newTeamAssistV2Answer() {
  return {
    status: TeamAssistV2AnswerStatus.streaming,
    blocks: [],
    thinking: '',
    citations: [],
    tags: [],
    questionId: null,
    messageId: null,
    error: null,
    rating: null,
  };
}

/**
 * Folds one stream event into the answer. Text stays provisional until `done`:
 * `blocks_retracted` drops the blocks the model took back, and `error` drops
 * everything the turn streamed. An event this client doesn't know leaves the
 * answer as it was.
 */
export function reduceTeamAssistV2Event(answer, event) {
  switch (event.type) {
    case 'block_open':
      return {
        ...answer,
        blocks: upsertBlock(answer.blocks, { index: event.index, type: event.blockType, text: '', complete: false }),
      };

    case 'block_delta':
      return {
        ...answer,
        blocks: mapBlock(answer.blocks, event.index, block => ({ ...block, text: block.text + (event.delta || '') })),
      };

    case 'block_close':
      return { ...answer, blocks: mapBlock(answer.blocks, event.index, block => ({ ...block, complete: true })) };

    case 'block_full':
      return { ...answer, blocks: upsertBlock(answer.blocks, fullBlock(event.index, event.block || {})) };

    case 'blocks_retracted':
      return { ...answer, blocks: answer.blocks.filter(block => block.index < event.fromIndex) };

    case 'tool_executed':
      return {
        ...answer,
        blocks: answer.blocks.map(block => {
          if (block.type !== 'tool_use' || block.toolUseId !== event.toolUseId) {
            return block;
          }
          return { ...block, execution: execution(event) };
        }),
      };

    case 'tool_proposed':
      return {
        ...answer,
        blocks: answer.blocks.map(block => {
          if (block.type !== 'tool_use' || block.toolUseId !== event.toolUseId) {
            return block;
          }
          return { ...block, proposal: newProposal(event) };
        }),
      };

    case 'thinking_delta':
      return { ...answer, thinking: answer.thinking + (event.delta || '') };

    case 'done':
      return {
        ...answer,
        status: TeamAssistV2AnswerStatus.done,
        questionId: event.questionId || null,
        messageId: event.messageId || null,
        citations: event.citations || [],
        tags: event.tags || [],
      };

    case 'error':
      return {
        ...answer,
        status: TeamAssistV2AnswerStatus.error,
        blocks: [],
        thinking: '',
        questionId: event.questionId || answer.questionId,
        error: event.code
          ? { code: event.code }
          : { code: TeamAssistV2ErrorCode.server, message: event.message || null },
      };

    default:
      return answer;
  }
}

/**
 * A stored answer (a `role: 'assistant'` message from the load route) as a
 * finished answer. `executionsByToolUseId` holds the surfaced results from the
 * session's `tool_executed` messages, `proposalsByToolUseId` the proposals
 * from its `tool_proposed` messages, and `rating` the agent's rating on it.
 */
export function answerFromStoredMessage(
  message,
  { executionsByToolUseId = {}, proposalsByToolUseId = {}, rating = null } = {}
) {
  const content = message.content || [];
  return {
    ...newTeamAssistV2Answer(),
    status: TeamAssistV2AnswerStatus.done,
    blocks: content
      .map((block, index) => ({ block, index }))
      .filter(({ block }) => block.type !== 'thinking')
      .map(({ block, index }) => {
        const shaped = fullBlock(index, block);
        if (block.type !== 'tool_use') {
          return shaped;
        }
        const stored = executionsByToolUseId[block.toolUseId];
        const proposal = proposalsByToolUseId[block.toolUseId];
        return {
          ...shaped,
          ...(stored ? { execution: stored } : {}),
          ...(proposal ? { proposal } : {}),
        };
      }),
    thinking: content
      .filter(block => block.type === 'thinking')
      .map(block => block.text || '')
      .join(''),
    citations: message.citations || [],
    tags: message.tags || [],
    questionId: message.questionId || null,
    messageId: message.id || null,
    rating,
  };
}

/**
 * A proposal as glados describes it in a `tool_proposed` event or message,
 * before the agent decides.
 */
export function newProposal({ displayName, summary, inputSchema }) {
  return {
    displayName: displayName || '',
    summary: summary || '',
    inputSchema: inputSchema || null,
    decision: null,
    approvedInput: null,
    issues: null,
    refusal: null,
  };
}

/**
 * Where a `tool_use` block's proposal stands, or null for a call that wasn't
 * one. `isLive` is whether its turn is streaming, which tells a run in progress
 * from an approval whose outcome never arrived.
 */
export function proposalStatus(block, { isLive = false } = {}) {
  if (!block.proposal) {
    return null;
  }
  if (block.execution) {
    return block.execution.isError ? TeamAssistV2ProposalStatus.failed : TeamAssistV2ProposalStatus.succeeded;
  }
  if (block.proposal.decision === TeamAssistV2ProposalDecision.rejected) {
    return TeamAssistV2ProposalStatus.rejected;
  }
  if (block.proposal.decision === TeamAssistV2ProposalDecision.approved) {
    return isLive ? TeamAssistV2ProposalStatus.running : TeamAssistV2ProposalStatus.unconfirmed;
  }
  return TeamAssistV2ProposalStatus.pending;
}

/** The answer with `fn` applied to the proposal `toolUseId` names, if it holds it. */
export function updateProposal(answer, toolUseId, fn) {
  const isIt = block => block.toolUseId === toolUseId && Boolean(block.proposal);
  if (!answer.blocks.some(isIt)) {
    return answer;
  }
  return {
    ...answer,
    blocks: answer.blocks.map(block => (isIt(block) ? { ...block, proposal: fn(block.proposal) } : block)),
  };
}

function fullBlock(index, block) {
  if (block.type === 'tool_use') {
    return {
      index,
      type: 'tool_use',
      toolUseId: block.toolUseId,
      name: block.name,
      input: block.input,
      complete: true,
    };
  }
  return { index, type: block.type, text: block.text || '', complete: true };
}

function execution(event) {
  return { result: event.result, resultType: event.resultType || null, isError: event.isError === true };
}

function upsertBlock(blocks, next) {
  const existing = blocks.findIndex(block => block.index === next.index);
  if (existing === -1) {
    return [...blocks, next].sort((a, b) => a.index - b.index);
  }
  const copy = blocks.slice();
  copy[existing] = { ...copy[existing], ...next };
  return copy;
}

function mapBlock(blocks, index, fn) {
  return blocks.map(block => (block.index === index ? fn(block) : block));
}
